import React from 'react';

import { createStyles, Divider, LoadingOverlay, Paper, Text, ActionIcon, Title } from '@mantine/core';
import { IconChevronLeft } from '@tabler/icons-react';
import { ReactComponent as KiloLogo } from 'assets/svg/long_kilo_blue_white.svg';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import usePackage from 'hooks/package/usePackage';
import { useParams, useNavigate } from 'react-router-dom';
import { getAttendanceRuleSummary, getEndConditions } from 'utils/billing.helpers';

const useStyles = createStyles(theme => ({
  labelGrid: {
    display: 'grid',
    rowGap: theme.spacing.xs,
    columnGap: theme.spacing.md,
    gridTemplateColumns: '1fr 2fr',
    gridTemplateRows: 'auto',
    wordBreak: 'break-word',
    [theme.fn.smallerThan('sm')]: {
      fontSize: theme.fontSizes.sm,
    },
  },
  divider: {
    gridColumnStart: '1',
    gridColumnEnd: '3',
  },
  container: {
    width: '90%',
    margin: '0 auto',
    overflowY: 'auto',
    overflowX: 'hidden',
    height: 'calc(100vh - 80px)',
    paddingBottom: theme.spacing.md,
    maxWidth: '100%',
  },
}));

const PackageDetail = (): JSX.Element => {
  const { formatCurrency, dateFormat } = useLocalization();
  const { packageId } = useParams<{ packageId: string }>();
  const { classes } = useStyles();
  const navigate = useNavigate();

  const { data: packageData, isError, isLoading, isSuccess } = usePackage(packageId);

  const handleBackClick = (): void => {
    navigate('/products?tab=packages');
  };

  const attendanceRuleSummaries = packageData?.data
    ? getAttendanceRuleSummary(packageData.data.attendanceRules)
    : [];
  const endConditions = packageData?.data
    ? getEndConditions(packageData.data.attendanceRules, packageData.data.type)
    : null;

  if (isError) {
    return <>Something went wrong</>;
  }

  return (
    <>
      <LoadingOverlay radius='lg' visible={isLoading} />
      <div className='header'>
        <ActionIcon variant='light' radius='lg' size='xl' onClick={handleBackClick}>
          <IconChevronLeft size='1.625rem' />
        </ActionIcon>
        <Title order={3} color='gray.8' align='center'>
          {packageData?.data?.name ? `${packageData.data.name} Details` : 'Package Details'}
        </Title>
        <KiloLogo height='100%' width={50} />
      </div>
      {isSuccess && packageData.data && (
        <div className={classes.container}>
          <Paper shadow='xs' radius='lg' p='sm' mb='sm' pos='relative'>
            <div className={classes.labelGrid}>
              <Text size='sm' color='dimmed' fw={600} mb='xs'>
                Event Types
              </Text>
              <div />
              {packageData.data.attendanceRules && packageData.data.attendanceRules.length > 0 ? (
                attendanceRuleSummaries.map((rule, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider mx='-md' className={classes.divider} />}
                    <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                      Event Type
                    </Text>
                    <Text ta='left'>{rule.name}</Text>

                    {packageData.data.type === 'CREDIT_BASED' && (
                      <>
                        <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                          Sessions
                        </Text>
                        <Text ta='left'>{rule.attendance.replace('sessions', '')}</Text>
                      </>
                    )}

                    {packageData.data.type !== 'CREDIT_BASED' && (
                      <>
                        <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                          Attendance
                        </Text>
                        <Text ta='left'>{rule.attendance}</Text>
                        <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                          Validity
                        </Text>
                        <Text ta='left'>
                          {packageData?.data.startDate &&
                            `Valid for ${rule.validity}, starts on ${dayjs(
                              packageData?.data.startDate,
                            ).format(dateFormat)}`}
                          {!packageData?.data.startDate && (
                            <>
                              {`Valid for ${rule.validity}, starts ${
                                rule.delay === '-' ? 'immediately' : `after ${rule.delay}`
                              }`}
                            </>
                          )}
                        </Text>
                      </>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <>
                  <Divider mx='-md' className={classes.divider} />
                  <Text size='sm' color='dimmed' fw={600}>
                    No event types configured
                  </Text>
                </>
              )}
              {endConditions && (
                <>
                  <Divider mx='-md' className={classes.divider} />
                  <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                    Package Validity
                  </Text>
                  <Text ta='left'>{endConditions}</Text>
                </>
              )}
            </div>
          </Paper>

          <Paper shadow='xs' radius='lg' p='sm' mb='sm' pos='relative'>
            <div className={classes.labelGrid}>
              <Text size='sm' color='dimmed' fw={600} mb='xs'>
                Billing Plans
              </Text>
              <div />
              {packageData.data.billingPlans && packageData.data.billingPlans.length > 0 ? (
                packageData.data.billingPlans.map((plan, index) => (
                  <React.Fragment key={plan.billingPlanId}>
                    {index > 0 && <Divider mx='-md' className={classes.divider} />}
                    <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                      Plan Name
                    </Text>
                    <Text ta='left'>{plan.name}</Text>
                    <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                      Total Price
                    </Text>
                    <Text ta='left'>{formatCurrency(plan.totalPrice / 100)}</Text>
                    <Text color='dimmed' fs='italic' fz='sm' ta='left'>
                      Number of Installments
                    </Text>
                    <Text ta='left'>{plan.numberOfInstallments}</Text>
                  </React.Fragment>
                ))
              ) : (
                <>
                  <Divider mx='-md' className={classes.divider} />
                  <Text size='sm' color='dimmed' fw={600}>
                    No billing plans configured
                  </Text>
                </>
              )}
            </div>
          </Paper>
        </div>
      )}
    </>
  );
};

export default PackageDetail;
